/**
 * Editing who you are.
 *
 * Everything here is held in local state and sent in one PATCH, so a half-filled
 * form never reaches the server and Cancel really does cancel. The games list is
 * the fiddly part: each entry carries a rank, an in-game name and up to four
 * roles, and the picker only offers games you have not already added.
 */

import { useState } from 'react';
import { gameIconArt } from '@/lib/gameArt';
import type { GameEntry } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { GameMark } from './GameMark';
import { Select } from './Select';
import { useSession } from '@/stores/session';
import { Button, Field, IconButton, Input, Modal, Toggle, useToast } from './ui';
import * as Icons from '@/icons';
import { EmojiField } from './EmojiField';

const MAX_LANGUAGES = 6;
const MAX_ROLES = 4;

export function EditProfile({ onClose, inline, section = 'all' }: {
  onClose: (changed: boolean) => void;
  /**
   * Which half to draw. The games list is long enough to deserve its own
   * screen in settings rather than being the tail of a page about your name.
   */
  section?: 'all' | 'about' | 'games';
  /**
   * Rendered straight onto the settings page rather than in a dialog.
   * Everything about your profile is then set up in one place, which is
   * where people go looking for it.
   */
  inline?: boolean;
}) {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const patchMe = useSession((s) => s.patchMe);
  const toast = useToast();
  /* Whether the picker is open. The search inside it is the picker's own
     business, so it resets every time - which is what you want. */
  const [langPicker, setLangPicker] = useState(false);

  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [bio, setBio] = useState(me?.bio ?? '');
  const [region, setRegion] = useState(me?.region ?? '');
  const [playstyle, setPlaystyle] = useState(me?.playstyle ?? '');
  const [mic, setMic] = useState(!!me?.mic);
  const [languages, setLanguages] = useState<string[]>(me?.languages ?? []);
  const [games, setGames] = useState<GameEntry[]>(() => (me?.games ?? []).map((g) => ({ ...g })));
  const [busy, setBusy] = useState(false);

  if (!me) return null;

  const remaining = (catalog?.games ?? []).filter((g) => !games.some((x) => x.id === g.id));

  function toggleLanguage(lang: string) {
    setLanguages((list) => {
      if (list.includes(lang)) return list.filter((l) => l !== lang);
      if (list.length >= MAX_LANGUAGES) {
        toast('Six languages is the maximum', 'warn');
        return list;
      }
      return [...list, lang];
    });
  }

  function patchGame(index: number, patch: Partial<GameEntry>) {
    setGames((list) => list.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  }

  async function save() {
    setBusy(true);
    try {
      // patchMe stores whatever the server returns, so the form and the rest
      // of the app cannot disagree about what was actually saved.
      await patchMe({
        displayName: displayName.trim(),
        bio,
        region,
        playstyle,
        languages,
        mic,
        games
      });
      toast('Profile saved', 'good');
      onClose(true);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const body = (
      <div className="flex flex-col gap-[var(--gap)]">
        {section !== 'games' ? (
        <>
        {/*
          Your status is not here.

          It was a plain text box that saved with the rest of the form, while
          the sidebar has the whole thing - the states, when the line clears
          itself, and the few you keep and rotate. Two places to set one
          thing, one of them worse, and the worse one silently overwriting
          the better one every time this form was saved.
        */}
        <Field label="Display name">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={32} />
        </Field>

        <Field label="Bio" hint={bio.length + ' / 400'}>
          <EmojiField
            textarea
            value={bio}
            onChange={setBio}
            maxLength={400}
            placeholder="How do you play? What are you looking for?"
          />
        </Field>

        <div className="grid gap-[var(--gap)] sm:grid-cols-2">
          <Field label="Region">
            <Select
              lead="globe"
              value={region}
              onChange={setRegion}
              options={(catalog?.regions ?? []).map((r) => ({ value: r.id, label: r.name }))}
            />
          </Field>
          <Field label="Playstyle">
            <Select
              lead="target"
              search={false}
              value={playstyle}
              onChange={setPlaystyle}
              options={(catalog?.playstyles ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
          </Field>
        </div>

        {/*
          Six of a hundred and eleven, without a wall of chips.

          Every version of this put the whole list on the page in some form -
          twelve chips and a fold, then a search that replaced them, then a
          scrolling box. All of them had the same problem: the languages you
          have picked, which is the only part you look at afterwards, were
          mixed in with the ninety you have not.

          So they are separate things now. Your picks are chips with a cross,
          and everything else is behind one button that opens a list with a
          search in it. The row is three chips tall whatever the catalogue
          does, and the hundredth language is two keystrokes away.
        */}
        <Field label="Languages" hint={languages.length + ' of ' + MAX_LANGUAGES}>
          <div className="flex flex-wrap items-center gap-1.5">
            {languages.map((lang) => (
              <span
                key={lang}
                className="inline-flex items-center gap-1 rounded-full bg-accent-soft py-1 pr-1 pl-2.5 text-[12.5px] font-semibold text-accent"
              >
                {lang}
                <button
                  type="button"
                  aria-label={'Remove ' + lang}
                  onClick={() => toggleLanguage(lang)}
                  className="grid h-[18px] w-[18px] place-items-center rounded-full outline-none transition-[background-color] duration-[var(--t-hover)] hover:bg-[color-mix(in_srgb,var(--accent)_25%,transparent)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                >
                  <Icon name="close" size={11} />
                </button>
              </span>
            ))}

            <button
              type="button"
              onClick={() => setLangPicker(true)}
              disabled={languages.length >= MAX_LANGUAGES}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-semibold text-mute outline-none shadow-[inset_0_0_0_1px_var(--line)] transition-[background-color,color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)] hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:cursor-not-allowed disabled:opacity-45"
            >
              <Icon name="plus" size={12} />
              {languages.length ? 'Add another' : 'Add a language'}
            </button>

            {languages.length >= MAX_LANGUAGES ? (
              <span className="text-[11.5px] text-ghost">That is the maximum.</span>
            ) : null}
          </div>
        </Field>

        {langPicker ? (
          <LanguagePicker
            all={catalog?.languages ?? []}
            picked={languages}
            max={MAX_LANGUAGES}
            onToggle={toggleLanguage}
            onClose={() => setLangPicker(false)}
          />
        ) : null}

        <Toggle
          checked={mic}
          onChange={setMic}
          label="I have a working microphone"
          note="Parties that require one will let you in"
        />
        </>
        ) : null}

        {section !== 'about' ? (
        <div className={section === 'games' ? '' : 'border-t border-line-soft pt-[var(--gap)]'}>
          {section === 'all' ? (
            <span className="mb-2 block text-[10.5px] font-bold tracking-[.07em] text-mute uppercase">
              Games
            </span>
          ) : null}

          <div className="flex flex-col gap-2">
            {games.map((entry, index) => {
              const meta = catalog?.games.find((g) => g.id === entry.id);
              return (
                <div
                  key={entry.id}
                  className="rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-3"
                >
                  <div className="mb-2 flex items-center gap-2.5">
                    <GameMark id={entry.id} size={28} />
                    <strong className="flex-1 text-[13px]">{meta?.name ?? entry.id}</strong>
                    <IconButton
                      label="Remove game"
                      className="h-7 w-7"
                      onClick={() => setGames((l) => l.filter((_, i) => i !== index))}
                    >
                      <Icons.Trash size={14} />
                    </IconButton>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <Select
                      lead="trending"
                      value={entry.rank ?? ''}
                      onChange={(v) => patchGame(index, { rank: v })}
                      options={[
                        { value: '', label: 'No rank' },
                        ...(meta?.ranks ?? []).map((r) => ({ value: r, label: r }))
                      ]}
                    />
                    <Input
                      value={entry.ign ?? ''}
                      onChange={(e) => patchGame(index, { ign: e.target.value })}
                      placeholder="in-game name"
                    />
                  </div>

                  {meta?.roles?.length ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {meta.roles.map((role) => {
                        const on = (entry.roles ?? []).includes(role);
                        return (
                          <Chip
                            key={role}
                            small
                            on={on}
                            onClick={() => {
                              const roles = entry.roles ?? [];
                              if (on) patchGame(index, { roles: roles.filter((r) => r !== role) });
                              else if (roles.length < MAX_ROLES) patchGame(index, { roles: [...roles, role] });
                              else toast('Four roles per game is the maximum', 'warn');
                            }}
                          >
                            {role}
                          </Chip>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}

            {remaining.length ? (
              <Select
                lead="plus"
                placeholder="Add a game"
                value=""
                onChange={(v) => {
                  if (!v) return;
                  setGames((l) => [...l, { id: v, rank: '', roles: [], ign: '', hours: 0 }]);
                }}
                options={remaining.map((g) => ({ value: g.id, label: g.name, icon: g.icon, color: g.color, art: gameIconArt(g.id) ?? undefined }))}
              />
            ) : null}
          </div>
        </div>
        ) : null}
      </div>
  );

  // Inline: the same form, with its own save row instead of a dialog footer.
  if (inline) {
    return (
      <div className="flex flex-col gap-[var(--gap)]">
        {body}
        <div className="flex items-center gap-2 border-t border-line-soft pt-[var(--gap)]">
          <span className="flex-1 text-[12.5px] text-mute">This is what everyone else sees.</span>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Modal
      wide
      title="Edit profile"
      subtitle="This is what everyone else sees."
      onClose={() => onClose(false)}
      footer={
        <>
          <Button kind="ghost" onClick={() => onClose(false)}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}

/**
 * Picking from a hundred and eleven.
 *
 * A list this long has exactly two access patterns: "the one everybody here
 * speaks", which wants to be the first thing on screen, and "mine, which is
 * not in your top twelve", which wants a search box. Chips on the settings
 * page served neither - the common ones were mixed in with the rest, and
 * finding yours meant reading five rows.
 *
 * So: the two dozen a Pugo lobby actually holds are kept in the order the
 * catalogue gives them and shown first under their own heading, everything
 * else is alphabetical under another, and one search filters both. Your
 * picks stay visible at the top the whole time, because the thing you most
 * want to know while choosing a sixth is what the other five were.
 */
function LanguagePicker({ all, picked, max, onToggle, onClose }: {
  all: string[];
  picked: string[];
  max: number;
  onToggle: (lang: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  /* The catalogue is already sorted: the common ones first, then A-Z. The
     split point is where it stops being deliberate and starts being
     alphabetical - see LANGUAGES in server/catalog.js. */
  const COMMON = 24;
  const common = all.slice(0, COMMON).filter((l) => l.toLowerCase().includes(q));
  const rest = all.slice(COMMON).filter((l) => l.toLowerCase().includes(q));
  const full = common.length + rest.length === 0;

  const atMax = picked.length >= max;

  function Row({ lang }: { lang: string }) {
    const on = picked.includes(lang);
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        disabled={!on && atMax}
        onClick={() => onToggle(lang)}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-[calc(9px*var(--radius-mul,1))] px-2.5 py-2 text-left text-[13.5px] outline-none',
          'transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
          'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
          on ? 'bg-accent-soft font-semibold text-accent' : 'text-dim hover:bg-surface-2 hover:text-ink',
          !on && atMax && 'cursor-not-allowed opacity-40 hover:bg-transparent'
        )}
      >
        <span
          className={cn(
            'grid h-[17px] w-[17px] flex-none place-items-center rounded-[5px]',
            on ? 'bg-accent text-[var(--accent-fg)]' : 'shadow-[inset_0_0_0_1.5px_var(--line)]'
          )}
        >
          {on ? <Icon name="check" size={11} /> : null}
        </span>
        <span className="min-w-0 flex-1 truncate">{lang}</span>
      </button>
    );
  }

  return (
    <Modal title="Languages you speak" subtitle={"Up to " + max + ", shown on your profile and used to match you into parties."} onClose={onClose} wide>
      <div className="flex min-h-0 flex-col gap-3">
        {/* Your picks, always in view. Choosing a sixth is a decision about
            the other five, so they cannot be the thing you scroll away. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {picked.length ? (
            picked.map((lang) => (
              <span
                key={lang}
                className="inline-flex items-center gap-1 rounded-full bg-accent-soft py-1 pr-1 pl-2.5 text-[12.5px] font-semibold text-accent"
              >
                {lang}
                <button
                  type="button"
                  aria-label={'Remove ' + lang}
                  onClick={() => onToggle(lang)}
                  className="grid h-[18px] w-[18px] place-items-center rounded-full outline-none transition-[background-color] duration-[var(--t-hover)] hover:bg-[color-mix(in_srgb,var(--accent)_25%,transparent)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                >
                  <Icon name="close" size={11} />
                </button>
              </span>
            ))
          ) : (
            <span className="text-[12.5px] text-ghost">Nothing picked yet.</span>
          )}
          <span className="min-w-4 flex-1" />
          <span className={cn('text-[12px] font-semibold', atMax ? 'text-warn' : 'text-ghost')}>
            {picked.length} of {max}
          </span>
        </div>

        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={'Search ' + all.length + ' languages'}
          aria-label="Search languages"
        />

        {/* One scroller, bounded, so the dialog is the same height whatever
            the search matches - a box that resizes as you type is unusable
            because the thing you were aiming at moves. */}
        <div className="-mr-1 max-h-[46vh] min-h-[220px] overflow-y-auto pr-1">
          {full ? (
            <p className="m-0 px-2 py-8 text-center text-[13px] text-mute">
              Nothing matches “{query.trim()}”.
            </p>
          ) : null}

          {common.length ? (
            <>
              <p className="m-0 mb-1 px-2.5 pt-1 text-[11px] font-bold tracking-[.07em] text-ghost uppercase">
                Most spoken here
              </p>
              <div className="mb-2 grid gap-0.5 sm:grid-cols-2">
                {common.map((lang) => <Row key={lang} lang={lang} />)}
              </div>
            </>
          ) : null}

          {rest.length ? (
            <>
              <p className="m-0 mb-1 px-2.5 pt-1 text-[11px] font-bold tracking-[.07em] text-ghost uppercase">
                Everything else
              </p>
              <div className="grid gap-0.5 sm:grid-cols-2">
                {rest.map((lang) => <Row key={lang} lang={lang} />)}
              </div>
            </>
          ) : null}
        </div>

        <div className="flex justify-end">
          <Button kind="primary" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function Chip({ on, small, onClick, children }: {
  on: boolean;
  small?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border font-semibold transition-colors',
        small ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-[12px]',
        on
          ? 'border-accent bg-[color-mix(in_srgb,var(--accent)_16%,transparent)] text-accent-hi'
          : 'border-line bg-surface-2 text-mute hover:border-accent-line hover:text-dim'
      )}
    >
      {children}
    </button>
  );
}

